import React, { useState } from 'react'
import { Tooltip } from '@material-ui/core'

import {
  CheckBox,
  TableFooter,
  TableBodyStyled,
  TableCellStyled,
  TableContainerStyled,
  TableHeadStyled,
  TableRowStyled,
  TableStyled,
  Row
} from 'components'
import {
  DeleteIcon,
  StatusIcon,
  UploadIcon,
  ViewFileIcon
} from 'components/Icons'
import { useFile, useModal } from 'hooks'
import { Anexo } from 'services/GST/SegurancaDoTrabalho'
import { FORMAT } from 'utils'

import { COLORS, SPACING } from 'utils'

interface ITableMonthlyDocumentation {
  tableData: Anexo[]
  handleSelectAll?: (
    _cdContrato: string,
    _e: React.ChangeEvent<HTMLInputElement>
  ) => void
  handleUpdateCheckBox?: (_cdContrato: string, _tipo: string) => void
  handleDelete?: (_id?: number) => void
  handleDeleteDocument?: (_id?: number) => void
  handleUploadFile?: (_data: {
    file: File
    idAmrItemSegurancaContrato: number
  }) => void
}

const TableWorkSafety: React.FC<ITableMonthlyDocumentation> = ({
  tableData,
  handleDeleteDocument,
  handleUploadFile
}) => {
  const { showModal: showSystemModal } = useModal()
  const { viewFile } = useFile()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const openFileChoose = (idMovDocumento: number) => {
    document.getElementById(`file-upload-${idMovDocumento}`).click()
  }

  const setStatusColor = (cdStatus?: string): string => {
    if (!['Analisar', 'Aprovado', 'Recusado'].includes(cdStatus)) {
      return COLORS.gray
    }

    switch (cdStatus) {
      case 'Aprovado':
        return COLORS.success
      case 'Recusado':
        return COLORS.error
      case 'Analisar':
        return COLORS.alert
      default:
        return COLORS.gray
    }
  }

  const onChooseFileToUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    idAmrItemSegurancaContrato: number
  ) => {
    if (event.target.files[0]) {
      if (event.target.files[0].type.includes('pdf')) {
        const limitUpload = 2000000000 // 2GB

        if (event?.target?.files[0].size >= limitUpload) {
          showSystemModal({
            type: 'alert',
            message: 'Limite para o tamanho do arquivo atingido: 2GB portanto arquivo não pode ser anexado',
            hideButtonCancel: true,
            txtButtonConfirm: 'OK'
          })

          return
        }

        handleUploadFile({
          file: event.target.files[0],
          idAmrItemSegurancaContrato
        })

        event.target.value = ''
        return
      }

      showSystemModal({
        hideButtonCancel: true,
        message: 'Só é permitido anexar documentos em PDF.',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
    }
  }

  const handleStatus = (status: string) => {
    if (
      status === 'Green' ||
      status === 'Aprovado' ||
      status === 'Liberado' ||
      status === 'L'
    )
      return 'Aprovado'
    if (
      status === 'Yellow' ||
      status === 'Analisar' ||
      status === 'Analisando' ||
      status === 'PendenteDocumento' ||
      status === 'P'
    )
      return 'Em análise'
    if (status === 'Red' || status === 'Recusado' || status === 'B')
      return 'Reprovado'
    return 'Pendente envio'
  }

  return (
    <>
      <TableContainerStyled>
        <TableStyled>
          <TableHeadStyled>
            <TableRowStyled>
              <TableCellStyled
                padding="checkbox"
                align="center"
              ></TableCellStyled>
              <TableCellStyled>Tipo Documento</TableCellStyled>
              <TableCellStyled align="center">Data Anexada</TableCellStyled>
              <TableCellStyled align="center">Retorno Analise</TableCellStyled>
              <TableCellStyled align="center" padding="checkbox">
                Status
              </TableCellStyled>
              <TableCellStyled align="center">Ações</TableCellStyled>
            </TableRowStyled>
          </TableHeadStyled>
          <TableBodyStyled>
            {(rowsPerPage > 0
              ? tableData?.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
              : tableData
            )?.map((row: Anexo) => (
              <TableRowStyled key={row?.idAmrItemSegurancaContrato}>
                <TableCellStyled padding="checkbox" align="center">
                  <CheckBox
                    marginRight="auto"
                    marginLeft="auto"
                    checked={Boolean(row?.base64)}
                    disabled={['Analisar', 'Aprovado'].includes(row?.cdStatus)}
                  />
                </TableCellStyled>
                <TableCellStyled>
                  {row?.txDescricaoRelItemSeguranca || '- - - - - - - - -'}
                </TableCellStyled>
                <TableCellStyled align="center">
                  {FORMAT.formatDDMMYYYY(row?.dtAnexado)}
                </TableCellStyled>
                <TableCellStyled align="center">
                  {FORMAT.formatDDMMYYYY(row?.dtRetorno)}
                </TableCellStyled>
                <Tooltip
                  arrow
                  title={handleStatus(row?.cdStatus)}
                  placement="left"
                >
                  <TableCellStyled align="center" padding="checkbox">
                    <StatusIcon
                      value={row?.cdStatus}
                      colorIcon={setStatusColor(row?.cdStatus)}
                      marginLeft="auto"
                      marginRight="auto"
                    />
                  </TableCellStyled>
                </Tooltip>
                <TableCellStyled align="center">
                  <Row alignItems="center" justifyContent="flex-start">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(event) =>
                        onChooseFileToUpload(
                          event,
                          row?.idAmrItemSegurancaContrato
                        )
                      }
                      id={`file-upload-${row?.idAmrItemSegurancaContrato}`}
                      style={{ display: 'none' }}
                    />
                    <UploadIcon
                      key={row.idAmrItemSegurancaContrato}
                      onClick={() =>
                        openFileChoose(row?.idAmrItemSegurancaContrato)
                      }
                      colorIcon={COLORS.black}
                      cursor="pointer"
                      disabled={
                        ['Analisar', 'Aprovado'].includes(row?.cdStatus) ||
                        Boolean(row?.base64)
                      }
                    />
                    <ViewFileIcon
                      onClick={() =>
                        viewFile({
                          gedExternalID: row?.cdGedExternalId,
                          local: {
                            base64: row.base64,
                            filename: row.filename
                          }
                        })
                      }
                      marginLeft={SPACING.spacing15}
                      marginRight={SPACING.spacing15}
                      cursor="pointer"
                      disabled={
                        ['Pendente', null, ''].includes(row?.cdStatus) &&
                        !row?.base64
                      }
                      colorIcon={COLORS.black}
                    />
                    <DeleteIcon
                      onClick={() =>
                        handleDeleteDocument(row?.idAmrItemSegurancaContrato)
                      }
                      disabled={
                        ['Analisar', 'Aprovado', 'Recusado'].includes(
                          row?.cdStatus
                        ) || !row?.base64
                      }
                      cursor="pointer"
                    />
                  </Row>
                </TableCellStyled>
              </TableRowStyled>
            ))}
          </TableBodyStyled>
        </TableStyled>
      </TableContainerStyled>
      <TableFooter
        page={page}
        rows={tableData || []}
        typeFooter="offline"
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  )
}

export default TableWorkSafety
